import { Link } from 'react-router-dom';

import {
  ACHIEVEMENT_HEADLINE,
  GRADUATES_SERIES,
  LEARNING_OUTCOMES,
  RETENTION_SERIES,
} from '@/data/experience';
import { AnimatedCounter, ScrollReveal, SplitReveal } from '@/components/motion';

/** The three retention series rendered as small accessible column charts. */
const RETENTION_CHARTS = [
  { key: 'mba', title: 'MBA retention', bar: 'bg-brand-navy' },
  { key: 'dba', title: 'DBA retention', bar: 'bg-brand-gold' },
  { key: 'institution', title: 'Institution-wide', bar: 'bg-brand-navy/40' },
] as const;

/** Tallest graduate cohort — used to scale the growth strip bars. */
const GRADUATES_MAX = Math.max(...GRADUATES_SERIES.map((g) => g.count));

/**
 * AchievementRates (/experience §2 ★ ACBSP-mandated) — "Student achievement
 * rates". Leads with the four ACHIEVEMENT_HEADLINE count-up figures, then a
 * lightweight, fully accessible retention visualisation (three small div-bar
 * column charts across 2021–2025, every value printed as real text), a compact
 * institution-wide graduates growth strip (319 → 1,275), the two ACBSP 2025
 * learning-outcome cards, and a link out to the full authoritative figures.
 *
 * Motion: SplitReveal headline, ScrollReveal child-stagger for the stat row,
 * chart columns and outcome cards, AnimatedCounter for every proof figure.
 * All primitives are reduced-motion safe and never strand content hidden;
 * bar heights are inline (final height always rendered), so accuracy and
 * accessibility win over flourish. Every number matches @/data/experience
 * exactly and each figure carries its year/scope caption.
 */
export function AchievementRates() {
  return (
    <section className="border-t border-border bg-muted/30 py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-6">
        {/* ── Heading + lede ──────────────────────────────────────────── */}
        <header className="max-w-2xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Student achievement rates
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            Proven, published, and accredited.
          </SplitReveal>
          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              These are ACBSP-accredited MBA and DBA figures, reported by the institution for
              2021–2025 — retention, graduate growth and learning-outcome achievement, published
              rather than merely claimed.
            </p>
          </ScrollReveal>
        </header>

        {/* ── Headline count-up figures ───────────────────────────────── */}
        <ScrollReveal
          stagger={0.08}
          className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4"
        >
          {ACHIEVEMENT_HEADLINE.map((stat) => (
            <div
              key={stat.label}
              className="h-full rounded-2xl border border-border bg-background p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            >
              <div className="flex size-11 items-center justify-center rounded-xl bg-brand-navy/5 text-brand-navy">
                <stat.Icon aria-hidden className="size-5" />
              </div>
              <AnimatedCounter
                value={stat.value}
                decimals={stat.decimals ?? 0}
                suffix={stat.suffix}
                grouping
                className="mt-5 block font-display text-4xl leading-none font-semibold text-brand-navy md:text-5xl"
              />
              <p className="mt-3 text-sm leading-snug font-medium text-foreground">{stat.label}</p>
              <p className="mt-1 text-xs leading-snug text-muted-foreground">{stat.caption}</p>
            </div>
          ))}
        </ScrollReveal>

        {/* ── Retention visualisation, 2021–2025 ──────────────────────── */}
        <div className="mt-16 border-t border-border pt-12">
          <h3 className="font-display text-xl leading-tight font-semibold text-foreground">
            Retention rate, 2021–2025
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">
            ACBSP MBA and DBA retention alongside institution-wide retention (% of students
            retained).
          </p>

          <ScrollReveal
            stagger={0.1}
            className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3"
          >
            {RETENTION_CHARTS.map((chart) => (
              <div
                key={chart.key}
                className="rounded-2xl border border-border bg-background p-6 shadow-sm"
              >
                <p className="font-label text-xs tracking-[0.18em] text-muted-foreground uppercase">
                  {chart.title}
                </p>
                <div className="mt-5 flex h-40 items-end justify-between gap-2">
                  {RETENTION_SERIES.map((row) => {
                    const value = row[chart.key];
                    return (
                      <div
                        key={row.year}
                        className="flex h-full flex-1 flex-col items-center justify-end gap-2"
                      >
                        <span className="font-display text-xs font-semibold text-foreground tabular-nums">
                          {value.toFixed(1)}
                        </span>
                        <div
                          className={`w-full rounded-t-md ${chart.bar}`}
                          style={{ height: `${value}%` }}
                          role="img"
                          aria-label={`${chart.title}, ${row.year}: ${value.toFixed(1)} percent`}
                        />
                        <span className="text-xs text-muted-foreground tabular-nums">
                          {row.year}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </ScrollReveal>
        </div>

        {/* ── Institution-wide graduates growth strip ─────────────────── */}
        <div className="mt-14 rounded-2xl border border-border bg-background p-7 shadow-sm">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="font-label text-xs tracking-[0.18em] text-muted-foreground uppercase">
                Institution-wide graduates
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                Annual graduate cohort, all programmes, 2021–2025.
              </p>
            </div>
            <p className="font-display text-sm text-muted-foreground">
              <span className="font-semibold text-brand-navy tabular-nums">319</span>
              <span className="mx-2" aria-hidden>
                →
              </span>
              <span className="font-semibold text-brand-navy tabular-nums">1,275</span>
            </p>
          </div>

          <ScrollReveal
            stagger={0.08}
            className="mt-6 flex h-36 items-end justify-between gap-3"
          >
            {GRADUATES_SERIES.map((row) => (
              <div
                key={row.year}
                className="flex h-full flex-1 flex-col items-center justify-end gap-2"
              >
                <AnimatedCounter
                  value={row.count}
                  grouping
                  className="font-display text-xs font-semibold text-foreground tabular-nums"
                />
                <div
                  className="w-full rounded-t-md bg-brand-gold"
                  style={{ height: `${(row.count / GRADUATES_MAX) * 100}%` }}
                  role="img"
                  aria-label={`Institution-wide graduates, ${row.year}: ${row.count.toLocaleString('en-GB')}`}
                />
                <span className="text-xs text-muted-foreground tabular-nums">{row.year}</span>
              </div>
            ))}
          </ScrollReveal>
        </div>

        {/* ── ACBSP learning outcomes · 2025 ──────────────────────────── */}
        <ScrollReveal
          stagger={0.1}
          className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2"
        >
          {(
            [
              { programme: 'MBA', field: 'mba2025' },
              { programme: 'DBA', field: 'dba2025' },
            ] as const
          ).map((col) => (
            <div
              key={col.programme}
              className="h-full rounded-2xl border border-border bg-background p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            >
              <p className="font-display text-lg leading-tight font-semibold text-foreground">
                {col.programme}
              </p>
              <p className="font-label text-xs tracking-[0.18em] text-muted-foreground uppercase">
                ACBSP learning outcomes · 2025
              </p>
              <dl className="mt-6 flex flex-col gap-5">
                {LEARNING_OUTCOMES.map((outcome) => (
                  <div
                    key={outcome.outcome}
                    className="flex items-baseline justify-between gap-4 border-t border-border pt-4 first:border-t-0 first:pt-0"
                  >
                    <dt className="text-sm leading-snug text-muted-foreground">
                      {outcome.outcome}
                    </dt>
                    <dd>
                      <AnimatedCounter
                        value={outcome[col.field]}
                        decimals={2}
                        suffix="%"
                        grouping
                        className="font-display text-2xl leading-none font-semibold text-brand-navy"
                      />
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </ScrollReveal>

        {/* ── Sourcing caption + authoritative link ───────────────────── */}
        <div className="mt-10 flex flex-col gap-3 border-t border-border pt-8 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-muted-foreground">
            Figures are institution-reported for 2021–2025.
          </p>
          <Link
            to="/experience/student-achievement-rates"
            data-cursor
            className="inline-block text-sm font-medium text-brand-navy underline decoration-brand-gold decoration-2 underline-offset-4 transition-colors duration-300 hover:text-brand-gold motion-reduce:transition-none"
          >
            See the full ACBSP figures
          </Link>
        </div>
      </div>
    </section>
  );
}
